import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card } from "@/components/shared";
import AdminActivity from "@/components/AdminActivity";
import { inr, inrShort } from "@/lib/api";
import {
  PhoneCall, AlertTriangle, IndianRupee, Star, MessageSquareText, Sparkles,
  TrendingUp, Activity, Percent, Building2
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";

const COLORS = ["#C9A24B", "#059669", "#64748B", "#DC2626", "#38bdf8", "#a78bfa"];

const Kpi = ({ icon: Icon, label, value, sub, accent = "#C9A24B", testid }) => (
  <Card className="p-5 ync-fade-up">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500">{label}</p>
        <p data-testid={testid} className="font-display text-3xl text-white font-light mt-2 leading-none">{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-2">{sub}</p>}
      </div>
      <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: accent + "20" }}>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { branch, person, dateRange } = useApp();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard", { params: { branch: branch || "All", person: person || "All", date_from: dateRange.from || undefined, date_to: dateRange.to || undefined } });
      setData(res.data);
    } catch {}
  }, [branch, person, dateRange.from, dateRange.to]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-slate-400">Loading dashboard…</div>;
  const k = data.kpi;

  const cxData = Object.entries(data.cx_by_touchpoint).map(([name, v]) => ({ name, rating: v }));
  const concernData = Object.entries(data.concern_by_type).map(([name, value]) => ({ name, value }));
  const agingData = Object.entries(data.dues_aging).map(([name, count]) => ({ name, count, amount: data.dues_aging_amount[name] }));
  const pipelineData = Object.entries(data.pipeline).map(([name, value]) => ({ name: name.replace(/^\d+\.\s/, ""), value }));

  const up = data.upsell || { conversion: 0, accepted: 0, offered: 0, value: 0, cross_sell: 0 };
  const comp = data.complications || { open: 0, resolution_rate: 0, by_severity: {} };
  const pr = data.pricing || { avg_discount: 0, over_policy: 0 };
  const lf = data.lookfeel || { avg_score: 0, by_branch: {} };
  const lookfeelData = Object.entries(lf.by_branch).map(([name, score]) => ({ name, score }));
  const compData = Object.entries(comp.by_severity).map(([name, value]) => ({ name, value }));

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Daily working view — har person ka kaam, KPIs aur pending items ek jagah." />

      <AdminActivity />

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <Kpi testid="kpi-followups" icon={PhoneCall} label="Follow-Ups Due" value={k.fu_due_today} sub={`${k.fu_due_week} due this week`} accent="#C9A24B" />
        <Kpi testid="kpi-concerns" icon={AlertTriangle} label="Open Concerns" value={k.open_concerns}
          sub={`${k.severity_counts.Critical} critical · ${k.severity_counts.High} high`} accent="#DC2626" />
        <Kpi testid="kpi-dues" icon={IndianRupee} label="Pending Dues" value={inrShort(k.pending_dues_total)} sub={`${k.pending_dues_count} patients`} accent="#DC2626" />
        <Kpi testid="kpi-satisfaction" icon={Star} label="Avg Satisfaction" value={`${k.avg_satisfaction}`} sub="out of 10" accent="#059669" />
        <Kpi testid="kpi-feedback-rate" icon={MessageSquareText} label="Follow-up Completion" value={`${k.fu_completion}%`} sub={`Feedback rate ${k.feedback_rate}%`} accent="#059669" />
      </div>

      {/* Insights */}
      <Card className="p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-4 w-4 text-[#C9A24B]" />
          <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Auto Insights</span>
        </div>
        <div className="space-y-2">
          {data.insights.map((ins, i) => (
            <div key={i} data-testid="insight-item" className="flex items-start gap-2 text-sm text-slate-200">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#C9A24B] shrink-0" />
              {ins}
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5">
          <h3 className="text-sm text-slate-300 mb-4">Customer Experience by Touchpoint <span className="text-slate-500">(/5)</span></h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={cxData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 5]} tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="rating" fill="#C9A24B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm text-slate-300 mb-4">Dues Aging</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={agingData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }}
                cursor={{ fill: "#ffffff08" }} formatter={(v, n) => n === "amount" ? inr(v) : v} />
              <Bar dataKey="count" fill="#DC2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm text-slate-300 mb-4">Concern Volume by Type</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={concernData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                {concernData.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-3 mt-2">
            {concernData.map((e, i) => (
              <div key={i} className="flex items-center gap-1.5 text-xs text-slate-400">
                <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{e.name} ({e.value})
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="text-sm text-slate-300 mb-4">Patients by Journey Stage</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={pipelineData} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
              <XAxis type="number" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 11 }} width={110} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="value" fill="#059669" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Growth & Experience Quality */}
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="h-4 w-4 text-[#C9A24B]" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Growth & Experience Quality</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <Kpi testid="kpi-upsell-conv" icon={TrendingUp} label="Upsell Conversion" value={`${up.conversion}%`} sub={`${up.accepted}/${up.offered} accepted`} accent="#C9A24B" />
        <Kpi testid="kpi-upsell-value" icon={IndianRupee} label="Upsell Value Won" value={inrShort(up.value)} sub={`${up.cross_sell} cross-sell deals`} accent="#059669" />
        <Kpi testid="kpi-complications" icon={Activity} label="Open Complications" value={comp.open} sub={`${comp.resolution_rate}% resolved`} accent="#DC2626" />
        <Kpi testid="kpi-discount" icon={Percent} label="Avg Discount" value={`${pr.avg_discount}%`} sub={`${pr.over_policy} beyond policy`} accent={pr.over_policy ? "#DC2626" : "#C9A24B"} />
        <Kpi testid="kpi-lookfeel" icon={Building2} label="Look & Feel Score" value={lf.avg_score} sub="out of 5" accent="#059669" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card className="p-5">
          <h3 className="text-sm text-slate-300 mb-4">Look & Feel by Branch <span className="text-slate-500">(/5)</span></h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={lookfeelData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 5]} tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="score" fill="#059669" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <h3 className="text-sm text-slate-300 mb-4">Complications by Severity</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={compData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #334155", borderRadius: 8, color: "#fff" }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="value" fill="#DC2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
